<!-- src/views/clinical/ClinicalHistoryForm.vue -->
<template>
  <div class="clinical-history-form">
    <!-- Back button and page title -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <router-link :to="returnPath" class="btn btn-outline-primary">
        <i class="bi bi-arrow-left me-2"></i>Volver
      </router-link>
      <h1>{{ isEditMode ? 'Editar Historia Clínica' : 'Nueva Historia Clínica' }}</h1>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="d-flex justify-content-center my-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
    </div>

    <!-- Form content -->
    <div v-else class="row">
      <div class="col-md-8 mx-auto">
        <div class="card">
          <div class="card-body">
            <form @submit.prevent="submitForm">
              <!-- Alert for errors -->
              <div v-if="error" class="alert alert-danger mb-4">
                <i class="bi bi-exclamation-triangle-fill me-2"></i>
                {{ error }}
              </div>

              <!-- Patient information (read-only) -->
              <div class="mb-4">
                <h5 class="card-title">Información del Paciente</h5>
                <div v-if="patient" class="d-flex align-items-center mb-3">
                  <div class="avatar-placeholder me-3">
                    {{ getInitials(patient.first_name, patient.last_name) }}
                  </div>
                  <div>
                    <h6 class="mb-0">{{ patient.first_name }} {{ patient.last_name }}</h6>
                    <p v-if="patient.fecha_nacimiento" class="text-muted mb-0">
                      {{ formatAge(patient.fecha_nacimiento) }} años
                    </p>
                  </div>
                </div>

                <div v-if="!patient && !isEditMode" class="alert alert-warning">
                  <i class="bi bi-exclamation-triangle-fill me-2"></i>
                  Debe seleccionar un paciente para crear una historia clínica.
                </div>
              </div>

              <hr class="my-4">

              <div class="row g-3">
                <!-- Consultation Reason -->
                <div class="col-12">
                  <label for="consultationReason" class="form-label">Motivo de la Consulta</label>
                  <textarea id="consultationReason" class="form-control" v-model="form.consultation_reason" rows="3"
                    placeholder="Describa el motivo principal de la consulta"></textarea>
                </div>
              </div>

              <!-- Medical History -->
              <div class="col-12">
                <label for="medicalHistory" class="form-label">Antecedentes Médicos</label>
                <textarea id="medicalHistory" class="form-control" v-model="form.medical_history" rows="3"
                  placeholder="Enfermedades importantes, procedimientos quirúrgicos previos, etc"></textarea>
              </div>

              <!-- Family Medical History -->
              <div class="col-12">
                <label for="familyMedicalHistory" class="form-label">Antecedentes Familiares</label>
                <textarea id="familyMedicalHistory" class="form-control" v-model="form.family_medical_history" rows="3"
                  placeholder="Familiares cercanos con alguna enfermedad importante"></textarea>
              </div>

              <!-- Medications -->
              <div class="col-12">
                <label for="medications" class="form-label">Medicamentos</label>
                <textarea id="medications" class="form-control" v-model="form.medications" rows="3"
                  placeholder="Liste los medicamentos que actualmente toma el paciente"></textarea>
              </div>


              <!-- Clinical History form fields -->
              <div class="row g-3">

                <!-- Allergies -->
                <div class="col-12">
                  <label for="allergies" class="form-label">Alergias</label>
                  <textarea id="allergies" class="form-control" v-model="form.allergies" rows="3"
                    placeholder="Registre las alergias conocidas del paciente"></textarea>
                </div>

                <!-- Dental History Section -->
                <div class="col-12">
                  <h5 class="mb-3">Historial Dental</h5>


              <!-- Dental Medical History -->
              <div class="col-12">
                <label for="familyMedicalHistory" class="form-label">Antecedentes Odontológicos</label>
                <textarea id="familyMedicalHistory" class="form-control" v-model="form.dental_history" rows="3"
                  placeholder="Procedimientos realizados previamente"></textarea>
              </div>

                  <!-- Orthodontic Treatment -->
                  <div class="mb-3">
                    <label class="form-label">¿Ha tenido algún tratamiento de Ortodoncia previo?</label>
                    <div class="d-flex">
                      <div class="form-check me-3">
                        <input class="form-check-input" type="radio" name="orthodonticTreatment"
                          id="orthodonticTreatmentYes" value="si" v-model="form.previous_orthodontic_treatment">
                        <label class="form-check-label" for="orthodonticTreatmentYes">
                          Sí
                        </label>
                      </div>
                      <div class="form-check">
                        <input class="form-check-input" type="radio" name="orthodonticTreatment"
                          id="orthodonticTreatmentNo" value="no" v-model="form.previous_orthodontic_treatment">
                        <label class="form-check-label" for="orthodonticTreatmentNo">
                          No
                        </label>
                      </div>
                    </div>
                  </div>

                  <!-- Gum Bleeding -->
                  <div class="mb-3">
                    <label class="form-label">¿Le sangran las encías?</label>
                    <div class="d-flex">
                      <div class="form-check me-3">
                        <input class="form-check-input" type="radio" name="gumBleeding" id="gumBleedingYes" value="si"
                          v-model="form.gum_bleeding">
                        <label class="form-check-label" for="gumBleedingYes">
                          Sí
                        </label>
                      </div>
                      <div class="form-check">
                        <input class="form-check-input" type="radio" name="gumBleeding" id="gumBleedingNo" value="no"
                          v-model="form.gum_bleeding">
                        <label class="form-check-label" for="gumBleedingNo">
                          No
                        </label>
                      </div>
                    </div>
                  </div>

                  <!-- Smoking -->
                  <div class="mb-3">
                    <label class="form-label">¿Fuma?</label>
                    <div class="d-flex">
                      <div class="form-check me-3">
                        <input class="form-check-input" type="radio" name="smoking" id="smokingYes" value="si"
                          v-model="form.smoking">
                        <label class="form-check-label" for="smokingYes">
                          Sí
                        </label>
                      </div>
                      <div class="form-check">
                        <input class="form-check-input" type="radio" name="smoking" id="smokingNo" value="no"
                          v-model="form.smoking">
                        <label class="form-check-label" for="smokingNo">
                          No
                        </label>
                      </div>
                    </div>
                  </div>

                  <!-- Teeth Grinding -->
                  <div class="mb-3">
                    <label for="teethGrinding" class="form-label">¿Aprieta los dientes durante el día o noche?</label>
                    <textarea id="teethGrinding" class="form-control" v-model="form.teeth_grinding" rows="2"
                      placeholder="Describa si aprieta los dientes y en qué momentos"></textarea>
                  </div>

                  <!-- Habits -->
                  <div class="mb-3">
                    <label class="form-label">¿Presenta alguno de los siguientes hábitos?</label>
                    <div class="form-check">
                      <input class="form-check-input" type="checkbox" id="habitDigital" value="succion_digital"
                        v-model="selectedHabits">
                      <label class="form-check-label" for="habitDigital">
                        Succión Digital
                      </label>
                    </div>
                    <div class="form-check">
                      <input class="form-check-input" type="checkbox" id="habitLabial" value="succion_labial"
                        v-model="selectedHabits">
                      <label class="form-check-label" for="habitLabial">
                        Succión Labial
                      </label>
                    </div>
                    <div class="form-check">
                      <input class="form-check-input" type="checkbox" id="habitCarrillos" value="succion_carrillos"
                        v-model="selectedHabits">
                      <label class="form-check-label" for="habitCarrillos">
                        Succión de Carrillos
                      </label>
                    </div>
                    <div class="form-check">
                      <input class="form-check-input" type="checkbox" id="habitOnicofagia" value="onicofagia"
                        v-model="selectedHabits">
                      <label class="form-check-label" for="habitOnicofagia">
                        Onicofagia
                      </label>
                    </div>
                    <div class="form-check">
                      <input class="form-check-input" type="checkbox" id="habitMuerdeLapices" value="muerde_lapices"
                        v-model="selectedHabits">
                      <label class="form-check-label" for="habitMuerdeLapices">
                        Muerde lápices
                      </label>
                    </div>
                    <div class="form-check">
                      <input class="form-check-input" type="checkbox" id="habitMuerdeLengua" value="muerde_lengua"
                        v-model="selectedHabits">
                      <label class="form-check-label" for="habitMuerdeLengua">
                        Muerde lengua
                      </label>
                    </div>
                    <div class="form-check">
                      <input class="form-check-input" type="checkbox" id="ninguno" value="ninguno"
                        v-model="selectedHabits">
                      <label class="form-check-label" for="ninguno">
                        Ninguno
                      </label>
                    </div>
                  </div>

                  <!-- Respiratory Problems -->
                  <div class="mb-3">
                    <label for="respiratoryProblems" class="form-label">¿Presenta algún problema respiratorio?</label>
                    <textarea id="respiratoryProblems" class="form-control" v-model="form.respiratory_problems" rows="2"
                      placeholder="Dormir con la boca abierta, roncar al dormir, etc."></textarea>
                  </div>

                  <!-- Previous Dental Surgery -->
                  <div class="mb-3">
                    <label class="form-label">¿Ha tenido alguna cirugía dental previa?</label>
                    <div class="d-flex">
                      <div class="form-check me-3">
                        <input class="form-check-input" type="radio" name="previousDentalSurgery"
                          id="previousDentalSurgeryYes" value="si" v-model="form.previous_dental_surgery">
                        <label class="form-check-label" for="previousDentalSurgeryYes">
                          Sí
                        </label>
                      </div>
                      <div class="form-check">
                        <input class="form-check-input" type="radio" name="previousDentalSurgery"
                          id="previousDentalSurgeryNo" value="no" v-model="form.previous_dental_surgery">
                        <label class="form-check-label" for="previousDentalSurgeryNo">
                          No
                        </label>
                      </div>
                    </div>
                  </div>

                  <!-- Local Anesthetic Problems -->
                  <div class="mb-3">
                    <label class="form-label">¿Ha presentado problemas con anestésicos locales?</label>
                    <div class="d-flex">
                      <div class="form-check me-3">
                        <input class="form-check-input" type="radio" name="localAnestheticProblems"
                          id="localAnestheticProblemsYes" value="si" v-model="form.local_anesthetic_problems">
                        <label class="form-check-label" for="localAnestheticProblemsYes">
                          Sí
                        </label>
                      </div>
                      <div class="form-check">
                        <input class="form-check-input" type="radio" name="localAnestheticProblems"
                          id="localAnestheticProblemsNo" value="no" v-model="form.local_anesthetic_problems">
                        <label class="form-check-label" for="localAnestheticProblemsNo">
                          No
                        </label>
                      </div>
                    </div>
                  </div>

                  <!-- Coagulation Problems -->
                  <div class="mb-3">
                    <label class="form-label">¿Ha presentado problemas de coagulación?</label>
                    <div class="d-flex">
                      <div class="form-check me-3">
                        <input class="form-check-input" type="radio" name="coagulationProblems"
                          id="coagulationProblemsYes" value="si" v-model="form.coagulation_problems">
                        <label class="form-check-label" for="coagulationProblemsYes">
                          Sí
                        </label>
                      </div>
                      <div class="form-check">
                        <input class="form-check-input" type="radio" name="coagulationProblems"
                          id="coagulationProblemsNo" value="no" v-model="form.coagulation_problems">
                        <label class="form-check-label" for="coagulationProblemsNo">
                          No
                        </label>
                      </div>
                    </div>
                  </div>

                  <!-- Tooth Pain -->
                  <div class="mb-3">
                    <label for="toothPain" class="form-label">¿Presenta dolor o molestia en algún diente?</label>
                    <textarea id="toothPain" class="form-control" v-model="form.tooth_pain" rows="2"
                      placeholder="Describa el dolor o molestia en los dientes"></textarea>
                  </div>

                  <!-- Joint Pain -->
                  <div class="mb-3">
                    <label for="jointPain" class="form-label">¿Presenta dolor en su articulación?</label>
                    <textarea id="jointPain" class="form-control" v-model="form.joint_pain" rows="2"
                      placeholder="Intensidad, localización, naturaleza del dolor (constante, esporádico, etc."></textarea>
                  </div>

                  <!-- Oral Cavity Pain -->
                  <div class="mb-3">
                    <label for="oralCavityPain" class="form-label">¿Presenta dolor o molestia en algún otro sector de su
                      cavidad bucal?</label>
                    <textarea id="oralCavityPain" class="form-control" v-model="form.oral_cavity_pain" rows="2"
                      placeholder="Describa cualquier dolor o molestia en la cavidad bucal"></textarea>
                  </div>
                </div>
                
                <!--  Notes -->
                <div class="col-12">
                  <label for="notes" class="form-label">Notas</label>
                  <textarea id="notes" class="form-control" v-model="form.notes" rows="10"></textarea>
                </div>

                <!-- Form actions -->
                <div class="col-12 mt-4">
                  <div class="d-flex justify-content-end">
                    <button type="button" class="btn btn-outline-secondary me-2" @click="resetForm">
                      Cancelar
                    </button>
                    <button type="submit" class="btn btn-primary" :disabled="submitting || !isFormValid">
                      <span v-if="submitting" class="spinner-border spinner-border-sm me-2" role="status"
                        aria-hidden="true"></span>
                      {{ isEditMode ? 'Guardar cambios' : 'Guardar historia clínica' }}
                    </button>
                  </div>
                </div>

              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { clinicalHistoryService, patientService } from '@/services/api';

export default {
  name: 'ClinicalHistoryForm',
  data() {
    return {
      form: {
        patient: null,
        notes: '',
        consultation_reason: '',
        medical_history: '',
        dental_history: '',
        family_medical_history: '',
        medications: '',
        allergies: '',
        previous_orthodontic_treatment: '',
        gum_bleeding: '',
        smoking: '',
        teeth_grinding: '',
        habits: '',
        respiratory_problems: '',
        previous_dental_surgery: '',
        local_anesthetic_problems: '',
        coagulation_problems: '',
        tooth_pain: '',
        joint_pain: '',
        oral_cavity_pain: '',
        diagnostic: ''
      },
      originalForm: {},
      patient: null,
      selectedHabits: [],
      loading: true,
      submitting: false,
      error: null
    };
  },
  computed: {
    isEditMode() {
      return this.$route.path.includes('/edit');
    },
    clinicalHistoryId() {
      return this.$route.params.id;
    },
    returnPath() {
      if (this.isEditMode) {
        return `/clinical-histories/${this.clinicalHistoryId}`;
      } else if (this.patient) {
        return `/patients/${this.patient.id}`;
      } else {
        return '/clinical-histories';
      }
    },
    isFormValid() {
      // In edit mode, we always allow saving
      if (this.isEditMode) return true;

      // In create mode, we need a patient
      return this.form.patient !== null;
    },
  },
  async created() {
    try {
      if (this.isEditMode) {
        await this.fetchClinicalHistory();
      } else {
        // Check if a patient ID was provided in the query params
        const patientId = this.$route.query.patient;
        if (patientId) {
          await this.fetchPatient(patientId);
          this.form.patient = parseInt(patientId);
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
      this.error = 'No se pudieron cargar los datos necesarios. Por favor, intenta de nuevo.';
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async fetchClinicalHistory() {
      const response = await clinicalHistoryService.get(this.clinicalHistoryId);
      this.form = { ...response.data };
      this.originalForm = { ...response.data };

      this.selectedHabits = this.form.habits ? this.form.habits.split(',') : [];

      // Fetch patient data
      if (this.form.patient) {
        await this.fetchPatient(this.form.patient);
      }
    },
    async fetchPatient(patientId) {
      const response = await patientService.get(patientId);
      this.patient = response.data;
    },
    async submitForm() {
      this.submitting = true;
      this.error = null;
      
      // Update habits from selectedHabits array
      this.form.habits = this.selectedHabits.join(',');

      try {
        if (this.isEditMode) {
          await clinicalHistoryService.update(this.clinicalHistoryId, this.form);
          // Navigate to clinical history detail page
          this.$router.push(`/clinical-histories/${this.clinicalHistoryId}`);
        } else {
          // Send all form fields to the API
          const response = await clinicalHistoryService.create(this.form);
          // Navigate to the new clinical history's detail page
          this.$router.push(`/clinical-histories/${response.data.id}`);
        }
      } catch (error) {
        console.error('Error saving clinical history:', error);
        this.error = 'Error al guardar la historia clínica. Por favor, intenta de nuevo.';
      } finally {
        this.submitting = false;
      }
    },
    resetForm() {
      if (this.isEditMode) {
        // In edit mode, reset to original values
        this.form = { ...this.originalForm };
        this.selectedHabits = this.form.habits ? this.form.habits.split(',') : [];
      } else {
        // In create mode, clear the form but keep the patient
        const patientId = this.form.patient;
        this.form = {
          patient: patientId,
          notes: '',
          consultation_reason: '',
          medical_history: '',
          dental_history: '',
          family_medical_history: '',
          medications: '',
          allergies: '',
          previous_orthodontic_treatment: '',
          gum_bleeding: '',
          smoking: '',
          teeth_grinding: '',
          habits: '',
          respiratory_problems: '',
          previous_dental_surgery: '',
          local_anesthetic_problems: '',
          coagulation_problems: '',
          tooth_pain: '',
          joint_pain: '',
          oral_cavity_pain: '',
          diagnostic: ''
        };
        this.selectedHabits = [];
      }

      this.error = null;
    },
    getInitials(firstName, lastName) {
      const first = firstName ? firstName.charAt(0).toUpperCase() : '';
      const last = lastName ? lastName.charAt(0).toUpperCase() : '';
      return `${first}${last}`;
    },
    formatAge(dateString) {
      if (!dateString) return '';
      const birthDate = new Date(dateString);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();

      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }

      return age;
    }
  }
};
</script>

<style scoped>
.avatar-placeholder {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #e9ecef;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: #495057;
}
</style>